import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import { GridColDef, GridActionsCellItem, GridRowSelectionModel } from '@mui/x-data-grid';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Remove as RemoveIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import DataTable from '../components/DataTable';
import OrganizationMemberEditDialog from '../components/OrganizationMemberEditDialog';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface User {
  id: string;
  email: string;
  displayName: string;
  isVerified: boolean;
  systemRole: string;
  createdAt: string;
  organizationRole?: string;
  organizationMemberActive?: boolean;
  joinedAt?: string;
  organizations?: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
}

interface UserOrganization {
  userId: string;
  organizationId: string;
  role: string;
  joinedAt: string;
  isActive: boolean;
  organization: {
    id: string;
    name: string;
    slug: string;
  };
}

const Users: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string>('MEMBER');
  const { formatDateOnly, formatDateLong, formatDateTime, fromDateTimeLocal } = useDateFormat();
  
  // Determine if this is the organization-specific view (RLS) or platform admin view
  const isOrganizationView = location.pathname.includes('/org/');
  const isPlatformAdminView = !isOrganizationView;
  const [openDialog, setOpenDialog] = useState(false);
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [openOrganizationsDialog, setOpenOrganizationsDialog] = useState(false);
  const [openAddOrgDialog, setOpenAddOrgDialog] = useState(false);
  const [openInvitationDialog, setOpenInvitationDialog] = useState(false);
  const [openInvitationsListDialog, setOpenInvitationsListDialog] = useState(false);
  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [openAssignRoleDialog, setOpenAssignRoleDialog] = useState(false);
  const [openMemberEditDialog, setOpenMemberEditDialog] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openBulkCalendarDialog, setOpenBulkCalendarDialog] = useState(false);
  const [bulkCalendarOptions, setBulkCalendarOptions] = useState<{ id: string; name: string }[]>([]);
  const [bulkCalendarId, setBulkCalendarId] = useState<string | null>(null);
  const [bulkCalendarFrom, setBulkCalendarFrom] = useState('');
  const [bulkCalendarSaving, setBulkCalendarSaving] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [selectedRole, setSelectedRole] = useState('MEMBER');
  const [invitationForm, setInvitationForm] = useState({
    email: '',
    role: 'MEMBER'
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    password: '',
    systemRole: ''
  });
  const [registerFormData, setRegisterFormData] = useState({
    email: '',
    displayName: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
    if (isOrganizationView && organizationId) {
      fetchCurrentUserRole();
    }
  }, [organizationId, isOrganizationView]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let response;
      
      if (isOrganizationView && organizationId) {
        // Organization-specific view (RLS) - only users from selected org
        response = await api.getOrganizationUsers(organizationId);
      } else {
        // Platform admin view - all users with organization info
        response = await api.getUsers();
      }
      
      setUsers(response.data || []);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  const fetchUserOrganizations = async (userId: string) => {
    try {
      const response = await api.getUserOrganizations({ userId });
      setUserOrganizations(response.data || []);
    } catch (err: any) {
      console.error('Error fetching user organizations:', err);
      setError(err.response?.data?.message || 'Failed to fetch user organizations');
    }
  };

  const fetchCurrentUserRole = async () => {
    try {
      const response = await api.getUserOrganizations();
      if (response.success && response.data) {
        const userOrg = response.data.find((org: any) => org.organizationId === organizationId);
        if (userOrg) {
          setCurrentUserRole(userOrg.role || 'MEMBER');
        }
      }
    } catch (err: any) {
      console.error('Error fetching current user role:', err);
      setCurrentUserRole('MEMBER'); // Default to member if error
    }
  };

  // Helper function to check if current user can assign OWNER role
  const canAssignOwnerRole = () => {
    return currentUserRole === 'OWNER';
  };

  // Helper function to get available roles based on current user's permissions
  const getAvailableRoles = () => {
    const baseRoles = ['MEMBER', 'MANAGER'];
    if (canAssignOwnerRole()) {
      baseRoles.push('OWNER');
    }
    return baseRoles;
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
      } else {
        await api.createUser(formData);
      }
      setOpenDialog(false);
      setEditingUser(null);
      setFormData({ email: '', displayName: '', password: '', systemRole: '' });
      fetchUsers();
    } catch (err) {
      setError('Failed to save user');
      console.error('Error saving user:', err);
    }
  };

  const handleRegister = async () => {
    try {
      if (registerFormData.password !== registerFormData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      await api.registerUser({
        email: registerFormData.email,
        displayName: registerFormData.displayName,
        password: registerFormData.password
      });
      
      setOpenRegisterDialog(false);
      setRegisterFormData({ email: '', displayName: '', password: '', confirmPassword: '' });
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register user');
      console.error('Error registering user:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.deleteUser(id);
        fetchUsers();
      } catch (err) {
        setError('Failed to delete user');
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleOpenOrganizationsDialog = async (user: User) => {
    setSelectedUser(user);
    await fetchUserOrganizations(user.id);
    setOpenOrganizationsDialog(true);
  };

  const handleAddUserToOrganization = async () => {
    if (!selectedUser || !selectedOrganization) return;

    try {
      await api.createUserOrganization({
        userId: selectedUser.id,
        organizationId: selectedOrganization.id,
        role: selectedRole
      });
      
      setOpenAddOrgDialog(false);
      setSelectedOrganization(null);
      setSelectedRole('MEMBER');
      await fetchUserOrganizations(selectedUser.id);
      setError(null);
    } catch (err: any) {
      console.error('Error adding user to organization:', err);
      setError(err.response?.data?.message || 'Failed to add user to organization');
    }
  };

  const handleRemoveUserFromOrganization = async (userOrg: UserOrganization) => {
    if (!selectedUser) return;

    if (window.confirm(`Are you sure you want to remove ${selectedUser.displayName} from ${userOrg.organization.name}?`)) {
      try {
        await api.deleteUserOrganization(userOrg.userId, userOrg.organizationId);
        await fetchUserOrganizations(selectedUser.id);
        setError(null);
      } catch (err: any) {
        console.error('Error removing user from organization:', err);
        setError(err.response?.data?.message || 'Failed to remove user from organization');
      }
    }
  };

  const handleSendInvitation = async () => {
    if (!organizationId || !invitationForm.email.trim()) return;

    try {
      await api.inviteUserToOrganization({
        organizationId,
        email: invitationForm.email.trim(),
        role: invitationForm.role
      });
      
      setOpenInvitationDialog(false);
      setInvitationForm({ email: '', role: 'MEMBER' });
      setError(null);
      // Show success message
      alert('Invitation sent successfully!');
    } catch (err: any) {
      console.error('Error sending invitation:', err);
      const errorMessage = err.response?.data?.message || 'Failed to send invitation';
      setError(errorMessage);
      
      // If it's a duplicate invitation, show a more helpful message
      if (err.response?.status === 409) {
        setError('An invitation has already been sent to this email address. Please wait for them to accept it or try a different email.');
      }
    }
  };

  const fetchPendingInvitations = async () => {
    if (!organizationId) return;
    
    try {
      setLoadingInvitations(true);
      const response = await api.getOrganizationInvitations(organizationId);
      setPendingInvitations(response.data?.invitations || []);
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      setError(err.response?.data?.message || 'Failed to fetch invitations');
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (window.confirm('Are you sure you want to cancel this invitation?')) {
      try {
        await api.deleteOrganizationInvitation(invitationId);
        await fetchPendingInvitations(); // Refresh the list
        setError(null);
      } catch (err: any) {
        console.error('Error canceling invitation:', err);
        setError(err.response?.data?.message || 'Failed to cancel invitation');
      }
    }
  };

  const handleOpenInvitationsList = async () => {
    setOpenInvitationsListDialog(true);
    await fetchPendingInvitations();
  };

  const handleOpenResetPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setResetPasswordForm({ password: '', confirmPassword: '' });
    setOpenResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (!resetPasswordForm.password || !resetPasswordForm.confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (resetPasswordForm.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(resetPasswordForm.password)) {
      setError('Password must contain at least one uppercase letter, one lowercase letter, and one number');
      return;
    }

    try {
      await api.adminResetUserPassword(selectedUser.id, resetPasswordForm.password);
      setOpenResetPasswordDialog(false);
      setSelectedUser(null);
      setResetPasswordForm({ password: '', confirmPassword: '' });
      setError(null);
      alert('Password reset successfully!');
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setError(err.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleOpenAssignRoleDialog = (user: User) => {
    setSelectedUser(user);
    setSelectedOrganization(null);
    setSelectedRole('MEMBER');
    setOpenAssignRoleDialog(true);
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedOrganization) return;

    try {
      await api.createUserOrganization({
        userId: selectedUser.id,
        organizationId: selectedOrganization.id,
        role: selectedRole
      });
      
      setOpenAssignRoleDialog(false);
      setSelectedUser(null);
      setSelectedOrganization(null);
      setSelectedRole('MEMBER');
      fetchUsers(); // Refresh the users list
      setError(null);
      alert('Role assigned successfully!');
    } catch (err: any) {
      console.error('Error assigning role:', err);
      setError(err.response?.data?.message || 'Failed to assign role');
    }
  };

  const handleOpenMemberEditDialog = (user: User) => {
    setSelectedUser(user);
    setOpenMemberEditDialog(true);
  };

  const getColumns = (): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      { field: 'email', headerName: 'Email', flex: 1, resizable: true },
      { field: 'displayName', headerName: 'Display Name', flex: 1, resizable: true },
      { 
        field: 'isVerified', 
        headerName: 'Verified', 
        width: 100,
        resizable: true,
        renderCell: (params) => (
          <span style={{ color: params.value ? 'green' : 'red' }}>
            {params.value ? '✓' : '✗'}
          </span>
        )
      }
    ];

    // Add organization role column for organization-specific view
    if (isOrganizationView) {
      baseColumns.push({
        field: 'organizationRole',
        headerName: 'Organization Role',
        flex: 1,
        resizable: true,
        renderCell: (params) => {
          const role = params.value;
          const getRoleColor = (role: string) => {
            switch (role?.toLowerCase()) {
              case 'OWNER': return 'warning';
              case 'ADMIN': return 'primary';
              case 'MANAGER': return 'primary';
              case 'MEMBER': return 'default';
              default: return 'default';
            }
          };
          const getRoleDescription = (role: string) => {
            switch (role?.toLowerCase()) {
              case 'OWNER': return 'Full organization control';
              case 'ADMIN': return 'Organization administrator';
              case 'MANAGER': return 'Organization administrator';
              case 'MEMBER': return 'Basic organization member';
              default: return role || 'No role assigned';
            }
          };
          return (
            <Chip
              label={role || 'No role'}
              color={getRoleColor(role)}
              size="small"
              variant="outlined"
            />
          );
        }
      });
    }

    // Add organization column for platform admin view
    if (isPlatformAdminView) {
      baseColumns.push({
        field: 'organizations',
        headerName: 'Organizations',
        flex: 1,
        resizable: true,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 1 }}>
            {params.value?.map((org: any) => (
              <Chip
                key={org.id}
                label={`${org.name} (${org.role})`}
                size="small"
                variant="outlined"
              />
            )) || <Typography variant="body2" color="text.secondary">No organizations</Typography>}
          </Box>
        )
      });
    }

    // Only show system role for platform admin view
    if (isPlatformAdminView) {
      baseColumns.push(
        { field: 'systemRole', headerName: 'System Role', flex: 1, resizable: true }
      );
    }

    baseColumns.push(
      { 
        field: 'lastLoginAt', 
        headerName: 'Last Login', 
        flex: 1,
        resizable: true,
        valueFormatter: (params) => {
          if (!params.value) return 'Never';
          return formatDateTime(params.value);
        }
      },
      { 
        field: 'createdAt', 
        headerName: 'Created', 
        flex: 1,
        resizable: true,
        valueFormatter: (params) => formatDateOnly(params.value)
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: isPlatformAdminView ? 300 : 120,
        resizable: true,
        getActions: (params) => {
          const actions = [];
          
          // Only show organization management for platform admins
          if (isPlatformAdminView) {
            actions.push(
              <GridActionsCellItem
                icon={<BusinessIcon />}
                label="Organizations"
                onClick={() => handleOpenOrganizationsDialog(params.row)}
              />
            );
            actions.push(
              <GridActionsCellItem
                icon={<PersonAddIcon />}
                label="Assign Role"
                onClick={() => handleOpenAssignRoleDialog(params.row)}
              />
            );
          }
          
          // Add edit role action for organization view
          if (isOrganizationView) {
            actions.push(
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleOpenMemberEditDialog(params.row)}
              />
            );
          } else {
            actions.push(
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => {
                  setEditingUser(params.row);
                  setFormData({
                    email: params.row.email,
                    displayName: params.row.displayName,
                    password: '',
                    systemRole: params.row.systemRole || ''
                  });
                  setOpenDialog(true);
                }}
              />
            );
          }
          
          // Only allow deletion and password reset for platform admins
          if (isPlatformAdminView) {
            actions.push(
              <GridActionsCellItem
                icon={<LockIcon />}
                label="Reset Password"
                onClick={() => handleOpenResetPasswordDialog(params.row)}
              />
            );
            actions.push(
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDelete(params.row.id)}
              />
            );
          }
          
          return actions;
        }
      }
    );

    return baseColumns;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {isPlatformAdminView ? 'All Users (Platform Admin)' : `Organization Users`}
          </Typography>
          {isOrganizationView && (
            <Typography variant="body2" color="text.secondary">
              Showing users from the selected organization
            </Typography>
          )}
        </Box>
        {isPlatformAdminView ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingUser(null);
              setFormData({ email: '', displayName: '', password: '', systemRole: '' });
              setOpenDialog(true);
            }}
          >
            Add User
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {['OWNER', 'MANAGER'].includes(currentUserRole) && (
              <Button
                variant="outlined"
                disabled={rowSelectionModel.length === 0}
                onClick={async () => {
                  if (!organizationId) return;
                  try {
                    const res = await api.listWorkingCalendars(organizationId, false);
                    const list = (res?.data ?? res) as { id: string; name: string }[];
                    setBulkCalendarOptions(Array.isArray(list) ? list : []);
                    setBulkCalendarId(null);
                    setBulkCalendarFrom('');
                    setOpenBulkCalendarDialog(true);
                  } catch (e: unknown) {
                    const err = e as { response?: { data?: { message?: string } } };
                    setError(err.response?.data?.message || 'Failed to load calendars');
                  }
                }}
              >
                Set working calendar ({rowSelectionModel.length})
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={handleOpenInvitationsList}
            >
              View Invitations
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setOpenInvitationDialog(true)}
            >
              Send Invitation
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <DataTable
        rows={users}
        columns={getColumns()}
        loading={loading}
        checkboxSelection={Boolean(isOrganizationView && ['OWNER', 'MANAGER'].includes(currentUserRole))}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(m) => setRowSelectionModel(m)}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[5, 10, 25]}
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-row': {
            minHeight: '52px !important',
            maxHeight: 'none !important',
          },
          '& .MuiDataGrid-cell': {
            minHeight: '52px !important',
            maxHeight: 'none !important',
            padding: '8px 16px',
          }
        }}
      />

      {/* Edit/Create User Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            margin="normal"
            disabled={!!editingUser}
          />
          <TextField
            fullWidth
            label="Display Name"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            margin="normal"
          />
          {!editingUser && (
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
            />
          )}
          {/* Only show system role field for platform admins */}
          {isPlatformAdminView && (
            <FormControl fullWidth margin="normal">
              <InputLabel>System Role</InputLabel>
              <Select
                value={formData.systemRole}
                label="System Role"
                onChange={(e) => setFormData({ ...formData, systemRole: e.target.value })}
              >
                <MenuItem value="">No System Role</MenuItem>
                <MenuItem value="PLATFORM_ADMIN">Platform Administrator</MenuItem>
                <MenuItem value="PLATFORM_SUPPORT">Platform Support</MenuItem>
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register New User Dialog */}
      <Dialog open={openRegisterDialog} onClose={() => setOpenRegisterDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register New User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={registerFormData.email}
            onChange={(e) => setRegisterFormData({ ...registerFormData, email: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Display Name"
            value={registerFormData.displayName}
            onChange={(e) => setRegisterFormData({ ...registerFormData, displayName: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={registerFormData.password}
            onChange={(e) => setRegisterFormData({ ...registerFormData, password: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={registerFormData.confirmPassword}
            onChange={(e) => setRegisterFormData({ ...registerFormData, confirmPassword: e.target.value })}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegisterDialog(false)}>Cancel</Button>
          <Button onClick={handleRegister} variant="contained">
            Register
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Organizations Dialog */}
      <Dialog open={openOrganizationsDialog} onClose={() => setOpenOrganizationsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Organizations - {selectedUser?.displayName}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddOrgDialog(true)}
            >
              Add to Organization
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {userOrganizations.map((userOrg, index) => (
              <React.Fragment key={`${userOrg.userId}-${userOrg.organizationId}`}>
                <ListItem>
                  <ListItemText
                    primary={userOrg.organization.name}
                    secondary={`Role: ${userOrg.role} • Joined: ${formatDateOnly(userOrg.joinedAt)}`}
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={userOrg.role} 
                        color={userOrg.role === 'OWNER' ? 'warning' : userOrg.role === 'MANAGER' ? 'primary' : 'default'}
                        size="small"
                      />
                      <Tooltip title="Remove from Organization">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveUserFromOrganization(userOrg)}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < userOrganizations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {userOrganizations.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No organizations found"
                  secondary="This user is not a member of any organizations."
                />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrganizationsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add User to Organization Dialog */}
      <Dialog open={openAddOrgDialog} onClose={() => setOpenAddOrgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add {selectedUser?.displayName} to Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={organizations.filter(org => 
                !userOrganizations.some(userOrg => userOrg.organizationId === org.id)
              )}
              getOptionLabel={(option) => option.name}
              value={selectedOrganization}
              onChange={(_, newValue) => setSelectedOrganization(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  required
                  helperText="Choose an organization to add this user to"
                />
              )}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {getAvailableRoles().map(role => (
                  <MenuItem key={role} value={role}>
                    {role === 'MANAGER' ? 'Manager' : role === 'MEMBER' ? 'Member' : 'Owner'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddOrgDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddUserToOrganization} 
            variant="contained"
            disabled={!selectedOrganization}
          >
            Add to Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Invitation Dialog */}
      <Dialog open={openInvitationDialog} onClose={() => setOpenInvitationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Invitation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={invitationForm.email}
            onChange={(e) => setInvitationForm({ ...invitationForm, email: e.target.value })}
            margin="normal"
            required
            placeholder="Enter the email address to invite"
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={invitationForm.role}
              label="Role"
              onChange={(e) => setInvitationForm({ ...invitationForm, role: e.target.value })}
            >
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="MANAGER">Manager</MenuItem>
              <MenuItem value="OWNER">Owner</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            An invitation email will be sent to the specified email address. The recipient will be able to accept the invitation and join your organization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInvitationDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSendInvitation} 
            variant="contained"
            disabled={!invitationForm.email.trim()}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending Invitations List Dialog */}
      <Dialog open={openInvitationsListDialog} onClose={() => setOpenInvitationsListDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pending Invitations</DialogTitle>
        <DialogContent>
          {loadingInvitations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Typography>Loading invitations...</Typography>
            </Box>
          ) : pendingInvitations.length > 0 ? (
            <List>
              {pendingInvitations.map((invitation, index) => (
                <React.Fragment key={invitation.id}>
                  <ListItem>
                    <ListItemText
                      primary={invitation.email}
                      secondary={`Role: ${invitation.role} • Sent: ${formatDateOnly(invitation.createdAt)} • Expires: ${formatDateLong(invitation.expiresAt)}`}
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={invitation.role} 
                          color={invitation.role === 'OWNER' ? 'warning' : invitation.role === 'MANAGER' ? 'primary' : 'default'}
                          size="small"
                        />
                        <Tooltip title="Cancel Invitation">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleCancelInvitation(invitation.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < pendingInvitations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                No pending invitations found.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All invitations have been accepted or expired.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInvitationsListDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenInvitationsListDialog(false);
              setOpenInvitationDialog(true);
            }}
            variant="contained"
          >
            Send New Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={openResetPasswordDialog} onClose={() => setOpenResetPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password for {selectedUser?.displayName || selectedUser?.email}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reset the user's password and send them a notification email. They will need to use this new password to log in.
          </Alert>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={resetPasswordForm.password}
            onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, password: e.target.value })}
            margin="normal"
            required
            placeholder="Enter new password"
            helperText="Password must be at least 8 characters with uppercase, lowercase, and number"
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={resetPasswordForm.confirmPassword}
            onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
            margin="normal"
            required
            placeholder="Confirm new password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetPasswordDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={!resetPasswordForm.password || !resetPasswordForm.confirmPassword}
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={openAssignRoleDialog} onClose={() => setOpenAssignRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Organization Role to {selectedUser?.displayName || selectedUser?.email}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={organizations}
              getOptionLabel={(option) => option.name}
              value={selectedOrganization}
              onChange={(_, newValue) => setSelectedOrganization(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  required
                  helperText="Choose an organization to assign this user to"
                />
              )}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                label="Role"
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                {getAvailableRoles().map(role => (
                  <MenuItem key={role} value={role}>
                    {role === 'MANAGER' ? 'Manager' : role === 'MEMBER' ? 'Member' : 'Owner'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignRoleDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignRole} 
            variant="contained"
            disabled={!selectedOrganization}
          >
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBulkCalendarDialog} onClose={() => setOpenBulkCalendarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk set working calendar</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {rowSelectionModel.length} user(s) selected. Effective from is required (stored as UTC).
          </Typography>
          <Autocomplete
            options={bulkCalendarOptions}
            getOptionLabel={(o) => o.name}
            value={bulkCalendarOptions.find((c) => c.id === bulkCalendarId) || null}
            onChange={(_, v) => setBulkCalendarId(v?.id || null)}
            renderInput={(params) => <TextField {...params} label="Calendar" margin="normal" />}
          />
          <TextField
            label="Effective from"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={bulkCalendarFrom}
            onChange={(e) => setBulkCalendarFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkCalendarDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={bulkCalendarSaving || !bulkCalendarId || !bulkCalendarFrom.trim() || !organizationId}
            onClick={async () => {
              if (!organizationId || !bulkCalendarId) return;
              setBulkCalendarSaving(true);
              setError(null);
              try {
                const res = await api.bulkAssignWorkingCalendar(organizationId, {
                  userIds: rowSelectionModel as string[],
                  workingCalendarId: bulkCalendarId,
                  effectiveFrom: fromDateTimeLocal(bulkCalendarFrom)
                });
                const data = res?.data ?? res;
                const created = (data?.created as string[])?.length ?? 0;
                const skipped = (data?.skipped as string[])?.length ?? 0;
                const failed = (data?.failed as { userId: string }[])?.length ?? 0;
                setOpenBulkCalendarDialog(false);
                setRowSelectionModel([]);
                await fetchUsers();
                if (skipped > 0 || failed > 0) {
                  setError(`Bulk assign finished: created ${created}, skipped ${skipped}, failed ${failed}.`);
                }
              } catch (e: unknown) {
                const err = e as { response?: { data?: { message?: string } } };
                setError(err.response?.data?.message || 'Bulk assign failed');
              } finally {
                setBulkCalendarSaving(false);
              }
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {isOrganizationView && organizationId && (
        <OrganizationMemberEditDialog
          open={openMemberEditDialog}
          onClose={() => {
            setOpenMemberEditDialog(false);
            setSelectedUser(null);
          }}
          organizationId={organizationId}
          user={
            selectedUser
              ? {
                  id: selectedUser.id,
                  email: selectedUser.email,
                  displayName: selectedUser.displayName,
                  organizationRole: selectedUser.organizationRole,
                  organizationMemberActive: selectedUser.organizationMemberActive
                }
              : null
          }
          availableRoles={getAvailableRoles()}
          onSaved={() => {
            fetchUsers();
            setError(null);
          }}
        />
      )}
    </Box>
  );
};

export default Users; 